import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { addDays, addHours, isAfter } from 'date-fns'
import { API } from 'lib/api'
import { apiName } from 'config/api'
import { useLoginUser } from 'hooks/loginuser'


const HISTORY_LIMIT = 300

const getData = async (cond, offset, limit) => {
    const params = `cond=${cond}${cond === 'history' ? `&limit=${limit}&offset=${offset}` : ''}`
    const ret = await API.get(apiName, `/items/getFeedback?${params}`, {})

    const list = ret.records.map(r => {
        r.メディア公開日時 = new Date(r.メディア公開日時)
        r.一時保存締切日 = new Date(r.一時保存締切日)
        r.メディア投稿日時 = new Date(r.メディア投稿日時)
        r.クライアント公開日時 = new Date(r.クライアント公開日時)
        r.新着 = addDays(r.メディア公開日時, 2) > new Date()
        if (r.リリースNo) {
            r.path = `pressrelease/${r.リリースNo}`
        }
        if (r.ネタNo) {
            r.path = `newsletter/${r.ネタNo}`
        }
        if (r.企業探訪No) {
            r.path = `story/${r.企業探訪No}`
        }
        
        return r
    })
    
    if (cond === 'history') {
        return { totalCount: Number(ret.totalCount), list }
    }
    else {
        return list
    }
}

const getHistory = async () => {
    // 最初の取得
    const ret = await getData('history', 0, HISTORY_LIMIT)
    const list = ret.list || []

    // 残件数分を取得
    if (ret.totalCount && ret.totalCount > HISTORY_LIMIT) {
        const ret2 = await Promise.all(
            [...Array(Math.ceil((ret.totalCount - HISTORY_LIMIT) / HISTORY_LIMIT)).keys()]
            .map(n => getData('history', (n + 1) * HISTORY_LIMIT, HISTORY_LIMIT))
        )

        // 取得した残りのデータをまとめる
        list.push(...ret2.flatMap(r => r.list || []))
    }

    return list
}


export const useAllFeedbacks = () => {
    return useQuery(['feedback'], getData)
}

// フィードバック依頼一覧の取得
export const useFeedbackRequest = () => {
    return useQuery(['feedback', 'latest'], () => getData('latest'), {
        select : data => data
            .filter(d => d.媒体担当者ID === "")
            .filter(d => d.一時保存 === false)
            .filter(d => d.クライアント公開 === false)
            .sort((a, b) => b.メディア公開日時 - a.メディア公開日時)
    })
}

// フィードバック一時保存一覧の取得
export const useTemporaryFeedbacks = () => {
    const { data : loginuser } = useLoginUser()

    return useQuery(['feedback', 'accept'], () => getData('accept'), {
        select : data => data
            .filter(d => d.媒体担当者ID === loginuser?.媒体担当者ID)
            .filter(d => d.一時保存 === true)
            .sort((a, b) => b.一時保存締切日 - a.一時保存締切日)
    })
}

// ネタもと確認中のフィードバック一覧の取得
export const useCheckingFeedbacks = () => {
    const { data : loginuser } = useLoginUser()

    return useQuery(['feedback', 'accept'], () => getData('accept'), {
        select : data => data
            .filter(d => d.媒体担当者ID === loginuser?.媒体担当者ID)
            .filter(d => d.一時保存 === false)
            .filter(d => d.クライアント公開 === false)
            .sort((a, b) => b.メディア投稿日時 - a.メディア投稿日時)
    })
}

// フィードバック履歴一覧の取得
export const useFeedbackHistories = () => {
    const { data : loginuser } = useLoginUser()

    return useQuery(['feedback', 'history'], getHistory, {
        select : data => data
            .filter(d => d.媒体担当者ID === loginuser?.媒体担当者ID)
            .filter(d => d.一時保存 === false)
            .filter(d => d.クライアント公開 === true)
            .sort((a, b) => b.メディア投稿日時 - a.メディア投稿日時)
    })
}

// 新着フィードバック依頼件数取得
export const useNewFeedbackCount = () => {
    return useQuery(['feedback', 'latest'], () => getData('latest'), {
        select : (data) => data
            .filter(d => d.メディア公開)
            .filter(d => !d.一時保存)
            .filter(d => !d.クライアント公開)
            .filter(d => isAfter(addHours(d.メディア公開日時, 48), new Date()))
            .filter(d => !d.媒体担当者ID)
            .length,
    })
}

// 新着フィードバック依頼取得
export const useNewFeedbackList = () => {
    return useQuery(['feedback', 'latest'], () => getData('latest'), {
        select : (data) => data
            .filter(d => d.メディア公開)
            .filter(d => !d.一時保存)
            .filter(d => !d.クライアント公開)
            .filter(d => isAfter(addHours(d.メディア公開日時, 48), new Date()))
            .filter(d => !d.媒体担当者ID)
            .sort((a, b) => b.メディア公開日時 - a.メディア公開日時)
    })
}

// フィードバックレコード特定
export const useFeedbackByNo = (no) => {
    const { data: latest } = useQuery(['feedback', 'latest'], () => getData('latest'))
    const { data: accept } = useQuery(['feedback', 'accept'], () => getData('accept'))
    const { data: history } = useQuery(['feedback', 'history'], getHistory)
    const [feedback, setFeedback] = useState(null)

    useEffect(() => {
        if (!latest || !accept || !history) return
        setFeedback(
            latest.concat(accept).concat(history).find(d => d.フィードバックNo === no)
        )
    }, [latest, accept, history, no])

    return { data: feedback }
}

